/*
 * OPEN PnL, ONE RULE FOR EVERY SCREEN.
 *
 * Guillaume, 2026-09-22: the Open Positions table moved fast, the open-trade
 * tray moved slowly and the trade page read $0 until the close -- three
 * numbers for one position. The table priced from the live price stream on
 * every tick, the tray showed the server's figure polled every 30 s, and the
 * trade page showed realised PnL, which is zero while nothing has closed.
 *
 * Now all three use this: the live mark from the price stream when we have
 * one, and the server's own figure (priced at the venue's mark) until the
 * first live price for the symbol arrives -- never zero by default.
 */
export function openPnlOf(args: {
  markPrice: number;
  entry: number;
  size: number;
  isLong: boolean;
  fallback?: number | null;
}): number {
  const { markPrice, entry, size, isLong, fallback } = args;
  if (markPrice > 0 && entry > 0 && size > 0) {
    return isLong ? (markPrice - entry) * size : (entry - markPrice) * size;
  }
  return fallback ?? 0;
}

/*
 * The account's open PnL: the live-priced sum of its open positions, as the
 * tray lists them. The Dashboard's "open" figure and the tray's header total
 * both come from here, so they cannot disagree (Guillaume, 2026-09-22).
 */
export function livePositionsTotal(
  positions: { symbol: string; side: string; entry_price?: number; size?: number; unrealized_pnl?: number }[],
  markOf: (symbol: string) => number,
): number {
  return positions.reduce((sum, p) => sum + openPnlOf({
    markPrice: markOf(p.symbol),
    entry: p.entry_price || 0,
    size: p.size || 0,
    isLong: String(p.side || '').toUpperCase().startsWith('LONG'),
    fallback: p.unrealized_pnl,
  }), 0);
}

/****************
 * THE VIEWER'S OWN BOOK ANSWERS ONLY FOR THE VIEWER'S OWN TRADE. 2026-10-08.
 *
 * The trade page took the open PnL of the first position in the VIEWER's
 * broker book with the same symbol and side, whatever trade it showed. On a
 * trader's trade (the Dashboard, a trader's page, an asset page) that is the
 * viewer's number, not the trader's: exactly the case of someone copying that
 * trader, who holds the same coin on the same side. A Hyperliquid trader's
 * row took it before anything else (usesOwnVenueMark), so it showed it every
 * time; a MEXC or Binance trader's row only when it had no mark.
 *
 * Only a session's trade is the viewer's own: it carries its session
 * (subscription_id). Undefined otherwise, or when the book holds no match.
 ****************/
export function ownBookPnl(
  trade: Record<string, unknown>,
  positions: { symbol?: string; side?: string; unrealized_pnl?: number | string }[] | undefined,
): number | undefined {
  if (!(Number(trade.subscription_id) > 0)) return undefined;
  const want = String(trade.direction ?? trade.side ?? '').toUpperCase().startsWith('LONG') ? 'LONG' : 'SHORT';
  const match = (positions ?? []).find(
    (p) => p.symbol === trade.symbol && String(p.side || '').toUpperCase().startsWith(want.slice(0, 4)),
  );
  if (!match) return undefined;
  const v = Number(match.unrealized_pnl);
  return Number.isFinite(v) ? v : undefined;
}

/*
 * ONE TRADE'S OPEN PnL, from whatever shape that trade came in.
 *
 * Guillaume, 2026-09-22: the trade page, the session's open-trades table and
 * the tray must show the same number; "instead of having one place do the math
 * and transmit the value to all the other, every data point run his own
 * calculation". This is that one place for a TRADE row.
 *
 * Size: current_size when it is a real size. Closed-trade feeds send
 * current_size: 0, and `??` treats 0 as a size -- which made the trade page
 * fall back while the session table priced it.
 */
export function openTradePnl(
  trade: Record<string, unknown>,
  markOf: (symbol: string) => number,
  fallback?: number | null,
): number {
  const num = (v: unknown) => {
    const n = typeof v === 'string' ? parseFloat(v) : (v as number);
    return Number.isFinite(n) ? n : 0;
  };
  const size = [trade.current_size, trade.initial_size, trade.max_size, trade.size]
    .map(num).find((v) => v > 0) ?? 0;
  const entry = [trade.avg_entry_price, trade.entry_price, trade.entry].map(num).find((v) => v > 0) ?? 0;
  const dir = String(trade.direction ?? trade.side ?? trade.type ?? 'LONG').toUpperCase();
  return openPnlOf({
    markPrice: markOf(String(trade.symbol ?? '')),
    entry,
    size,
    isLong: dir.startsWith('LONG') || dir.startsWith('BUY'),
    fallback: fallback ?? (trade.unrealized_pnl as number | undefined) ?? null,
  });
}
